import { apiFetch } from './api';
import { ApiError } from './http';
import { readApiError } from './apiError';
import { CommerceConfig, Payer } from '../types/commerce';

export enum OrderStatus {
  Creating = 'creating',
  Created = 'created',
  ProviderUnknown = 'provider_unknown',
  Authorized = 'authorized',
  Captured = 'captured',
  Failed = 'failed',
}
export enum PaymentMode {
  Test = 'test',
  Live = 'live',
}
export enum StockEffect {
  None = 'none',
  Consumed = 'consumed',
}
export enum FulfillmentStatus {
  Pending = 'pending',
  /** Only payments from the earlier quote-less test checkout. */
  SkippedTest = 'skipped_test',
  Allocated = 'allocated',
  Cancelled = 'cancelled',
}
export enum ReleaseReason {
  Cancelled = 'cancelled',
  PaymentFailed = 'payment_failed',
}

export interface PaymentOrder {
  /**
   * The backend's verdict on this attempt:
   * - `completed`: captured.
   * - `retry_allowed`: the customer closed Checkout and the backend confirmed with
   *   Razorpay that nothing was captured, authorized or still processing, so a new
   *   attempt (a new Razorpay order) may start. A late capture is refunded.
   * - `pending`: anything else; check status again before paying a second time.
   */
  readonly recovery_disposition: 'pending' | 'retry_allowed' | 'completed';
  /** Who pays; every payment comes from a checkout quote, which always names one. */
  readonly payer: Payer;
  readonly sales_order_id?: string | null;
  readonly id: string;
  readonly order_id: string | null;
  readonly amount: number;
  readonly currency: 'INR';
  readonly status: OrderStatus;
  readonly payment_mode: PaymentMode;
  readonly stock_effect: StockEffect;
  readonly fulfillment_status: FulfillmentStatus;
  readonly expires_at: string;
}
export interface PaymentSelection {
  readonly inventory_id: string;
  readonly quantity: number;
}
export interface CreateOrderInput {
  /** Every payment starts from a server-priced checkout quote. */
  readonly quote_id: string;
  readonly request_id: string;
  readonly delivery_address_id: string;
  readonly items: readonly PaymentSelection[];
  readonly email: string;
  readonly phone: string;
}
export interface PaymentProof {
  readonly razorpay_payment_id: string;
  readonly razorpay_order_id: string;
  readonly razorpay_signature: string;
}
export interface PaymentApi {
  config(): Promise<CommerceConfig>;
  create(input: CreateOrderInput): Promise<PaymentOrder>;
  verify(proof: PaymentProof): Promise<PaymentOrder>;
  get(id: string): Promise<PaymentOrder>;
  cancel(id: string, reason?: ReleaseReason): Promise<PaymentOrder>;
  reconcile(id: string): Promise<PaymentOrder>;
}
export class PaymentApiError extends ApiError {
  constructor(message: string, status: number) {
    super(message, status);
    this.name = 'PaymentApiError';
  }
}
export function isOrderStatus(value: unknown): value is OrderStatus {
  return Object.values(OrderStatus).some((status) => status === value);
}
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function isPaymentRequestId(value: unknown): value is string {
  return typeof value === 'string' && uuidPattern.test(value);
}
function validPayer(value: unknown): value is Payer {
  if (typeof value !== 'object' || value === null) return false;
  return (
    'name' in value &&
    typeof value.name === 'string' &&
    !!value.name.trim() &&
    'email' in value &&
    typeof value.email === 'string' &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.email) &&
    'phone' in value &&
    typeof value.phone === 'string' &&
    /^\+91[0-9]{10}$/.test(value.phone)
  );
}
export function parsePaymentOrder(value: unknown): PaymentOrder {
  if (
    typeof value !== 'object' ||
    value === null ||
    !('payer' in value) ||
    !validPayer(value.payer) ||
    ('sales_order_id' in value &&
      value.sales_order_id !== null &&
      value.sales_order_id !== undefined &&
      !isPaymentRequestId(value.sales_order_id)) ||
    !('recovery_disposition' in value) ||
    !['pending', 'retry_allowed', 'completed'].includes(value.recovery_disposition as string) ||
    !('id' in value) ||
    !isPaymentRequestId(value.id) ||
    !('order_id' in value) ||
    !(
      value.order_id === null ||
      (typeof value.order_id === 'string' && /^order_[a-zA-Z0-9]+$/.test(value.order_id))
    ) ||
    !('amount' in value) ||
    typeof value.amount !== 'number' ||
    !Number.isSafeInteger(value.amount) ||
    value.amount < 100 ||
    !('currency' in value) ||
    value.currency !== 'INR' ||
    !('status' in value) ||
    !isOrderStatus(value.status) ||
    !('payment_mode' in value) ||
    !Object.values(PaymentMode).includes(value.payment_mode as PaymentMode) ||
    !('stock_effect' in value) ||
    !Object.values(StockEffect).includes(value.stock_effect as StockEffect) ||
    !('fulfillment_status' in value) ||
    !Object.values(FulfillmentStatus).includes(value.fulfillment_status as FulfillmentStatus) ||
    !('expires_at' in value) ||
    typeof value.expires_at !== 'string' ||
    !Number.isFinite(Date.parse(value.expires_at))
  ) {
    throw new PaymentApiError('Invalid payment response. Check payment status before retrying.', 502);
  }
  return {
    recovery_disposition: value.recovery_disposition as PaymentOrder['recovery_disposition'],
    payer: value.payer,
    sales_order_id: 'sales_order_id' in value ? (value.sales_order_id as string | null) : undefined,
    id: value.id,
    order_id: value.order_id,
    amount: value.amount,
    currency: value.currency,
    status: value.status,
    payment_mode: value.payment_mode as PaymentMode,
    stock_effect: value.stock_effect as StockEffect,
    fulfillment_status: value.fulfillment_status as FulfillmentStatus,
    expires_at: value.expires_at,
  };
}
export function parseCommerceConfig(value: unknown): CommerceConfig {
  if (
    typeof value !== 'object' ||
    value === null ||
    !('enabled' in value) ||
    typeof value.enabled !== 'boolean' ||
    !('payment_mode' in value) ||
    !Object.values(PaymentMode).includes(value.payment_mode as PaymentMode) ||
    !('key_id' in value) ||
    typeof value.key_id !== 'string'
  )
    throw new PaymentApiError('Invalid payment configuration', 502);
  return {
    enabled: value.enabled,
    payment_mode: value.payment_mode as PaymentMode,
    key_id: value.key_id,
  };
}
export class HttpPaymentApi implements PaymentApi {
  constructor(private readonly request: typeof apiFetch) {}
  private async send(path: string, init: RequestInit = {}): Promise<PaymentOrder> {
    const response = await this.request(path, init);
    if (!response.ok)
      throw new PaymentApiError(
        await readApiError(response, 'Payment could not be confirmed. Please check its status.'),
        response.status,
      );
    const data: unknown = await response.json();
    return parsePaymentOrder(data);
  }
  async config(): Promise<CommerceConfig> {
    const response = await this.request('/commerce/config');
    if (!response.ok)
      throw new PaymentApiError(
        await readApiError(response, 'Payments are not configured.'),
        response.status,
      );
    return parseCommerceConfig(await response.json());
  }
  create(input: CreateOrderInput): Promise<PaymentOrder> {
    return this.send('/create-order', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }
  verify(proof: PaymentProof): Promise<PaymentOrder> {
    return this.send('/verify-payment', {
      method: 'POST',
      body: JSON.stringify(proof),
    });
  }
  get(id: string): Promise<PaymentOrder> {
    return this.send(`/payment-orders/${encodeURIComponent(id)}`);
  }
  cancel(id: string, reason = ReleaseReason.Cancelled): Promise<PaymentOrder> {
    return this.send(`/payment-orders/${encodeURIComponent(id)}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }
  reconcile(id: string): Promise<PaymentOrder> {
    return this.send(`/payment-orders/${encodeURIComponent(id)}/reconcile`, {
      method: 'POST',
    });
  }
}
export const paymentApi: PaymentApi = new HttpPaymentApi(apiFetch);
